import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { axiosInstance } from "@/App";
import { 
  Mic, 
  MicOff, 
  Video,
  VideoOff,
  Users,
  DollarSign,
  Radio,
  Wifi,
  WifiOff,
  SwitchCamera,
  Camera,
  Clock3,
  Zap,
  Focus,
  Play,
  Square,
  QrCode,
  RefreshCw,
  LogOut,
  Share2,
  Pin,
  Sun,
  X
} from "lucide-react";
import { toast } from "sonner";
import {
  LiveKitRoom,
  useRemoteParticipants,
  RoomAudioRenderer,
  useRoomContext,
} from "@livekit/components-react";
import "@livekit/components-styles";
import { Track, RoomEvent, ParticipantEvent, VideoQuality } from "livekit-client";
import { Button } from "@/components/ui/button";
import ShowMeDonationDollarLayer, {
  normalizeShowMeDonationDollarCount,
} from "@/components/ShowMeDonationDollarLayer";
import LiveReactionLayer from "@/components/LiveReactionLayer";
import LiveViewerRoster from "@/components/LiveViewerRoster";
import StreamDiagnostics from "@/components/StreamDiagnostics";
import {
  getNewEnergyReactionCount,
  SHOWME_ENERGY_START_REACTION_COUNT,
} from "@/utils/showMeEnergy";
import { useResilientWebSocket } from "@/hooks/useResilientWebSocket";
import { getProModeTransitionDuration } from "@/utils/proModeTransitions";
import { SMOOTH_ZOOM_COMMAND_INTERVAL_MS } from "@/utils/smoothZoom";
import { canSwitchProModePreview, resolveProModePreviewDevice } from "@/utils/proModeSwitcher";
import {
  shouldRequestProCameraRepublish,
} from "@/utils/proModeCameraHealth";
import EducationQuestionQueue, { CreatorAudienceSpeakerControls } from "@/components/EducationQuestionQueue";
import CreatorSettingsPortal from "@/components/CreatorSettingsPortal";
import { getEventExperience, isVisibleEventCategory } from "@/config/eventCategories";
import {
  CreatorTipNotificationStack,
  SwipeChatModerationRow,
  useCreatorTipNotifications,
} from "@/components/LiveExperienceChrome";
import { STREAM_ORIENTATION } from "@/utils/eventCreation";
import {
  getProControlSubscription,
  normalizeProAudioMode,
  PRO_AUDIO_MODE_RAW,
} from "@/utils/proModeMedia";
import { mergeChatMessages } from "@/utils/chatMessages";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const PRO_MODE_COMMAND_TOPIC = 'showme-pro-control';
const PRO_MODE_SOCKET_COMMAND_TYPES = new Set([
  'switch_camera',
  'set_camera_enabled',
  'focus_camera',
  'set_zoom',
  'set_torch',
  'end_stream',
]);

const REACTION_TYPE_EMOJIS = {
  heart: "\u2764\ufe0f",
  clap: "\ud83d\udc4f",
  fire: "\ud83d\udd25",
  laugh: "\ud83d\ude02",
  wow: "\ud83d\ude2e"
};

// Program transition styles. Both feeds stay mounted for the full animation so
// the effect happens between cameras instead of animating the incoming feed
// after the program source has already changed.
const transitionStyles = `
  .program-feed {
    position: absolute;
    inset: 0;
    backface-visibility: hidden;
    transform: translateZ(0);
    will-change: opacity, transform;
  }
  .program-feed-incoming { opacity: 0; }
  @keyframes programFadeOut {
    0% { opacity: 1; }
    48%, 100% { opacity: 0; }
  }
  @keyframes programFadeIn {
    0%, 52% { opacity: 0; }
    100% { opacity: 1; }
  }
  @keyframes programDissolveOut {
    from { opacity: 1; }
    to { opacity: 0; }
  }
  @keyframes programDissolveIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes programBlendOut {
    from { opacity: 1; transform: scale(1); }
    to { opacity: 0; transform: scale(.97); }
  }
  @keyframes programBlendIn {
    from { opacity: 0; transform: scale(1.04); }
    to { opacity: 1; transform: scale(1); }
  }
  .transition-fade .program-feed-outgoing { animation: programFadeOut 560ms cubic-bezier(.45,0,.55,1) forwards; }
  .transition-fade .program-feed-incoming { animation: programFadeIn 560ms cubic-bezier(.45,0,.55,1) forwards; }
  .transition-dissolve .program-feed-outgoing { animation: programDissolveOut 680ms linear forwards; }
  .transition-dissolve .program-feed-incoming { animation: programDissolveIn 680ms linear forwards; }
  .transition-blend .program-feed-outgoing { animation: programBlendOut 760ms cubic-bezier(.4,0,.2,1) forwards; }
  .transition-blend .program-feed-incoming { animation: programBlendIn 760ms cubic-bezier(.4,0,.2,1) forwards; }
  .transition-cut .program-feed-outgoing { opacity: 0; }
  .transition-cut .program-feed-incoming { opacity: 1; }
`;

const TRANSITIONS = [
  { id: "cut", name: "Cut", icon: "CUT" },
  { id: "fade", name: "Fade", icon: "FADE" },
  { id: "dissolve", name: "Dissolve", icon: "DSLV" },
  { id: "blend", name: "Blend", icon: "BLND" },
];

// ============================================
// Low-level Video renderer using track.attach()
// ============================================
const DirectVideo = ({ participant, className = "", fit = "cover" }) => {
  const videoRef = useRef(null);
  const [hasVideo, setHasVideo] = useState(false);

  useEffect(() => {
    if (!participant) { setHasVideo(false); return; }

    const attachTrack = () => {
      try {
        const cameraPub = participant.getTrackPublication(Track.Source.Camera);
        const track = cameraPub?.track;
        if (track && videoRef.current) {
          track.attach(videoRef.current);
          videoRef.current.muted = true;
          videoRef.current.playsInline = true;
          const playPromise = videoRef.current.play();
          if (playPromise?.catch) {
            playPromise.catch((playError) => {
              console.warn('[ProVideo] remote video autoplay retry required', {
                participant: participant.identity,
                error: String(playError),
              });
            });
          }
          console.info('[ProVideo] remote camera attached', {
            participant: participant.identity,
            publicationMuted: cameraPub?.isMuted,
            readyState: track.mediaStreamTrack?.readyState,
            trackMuted: track.mediaStreamTrack?.muted,
          });
          setHasVideo(true);
        } else {
          setHasVideo(false);
        }
      } catch (attachError) {
        console.error('[ProVideo] failed to attach remote camera', {
          participant: participant?.identity,
          error: String(attachError),
        });
        setHasVideo(false);
      }
    };

    attachTrack();

    const onSubscribed = () => attachTrack();
    const onUnsubscribed = () => { setHasVideo(false); };

    try {
      participant.on(ParticipantEvent.TrackSubscribed, onSubscribed);
      participant.on(ParticipantEvent.TrackUnsubscribed, onUnsubscribed);
      participant.on(ParticipantEvent.TrackPublished, onSubscribed);
      participant.on(ParticipantEvent.TrackMuted, attachTrack);
      participant.on(ParticipantEvent.TrackUnmuted, attachTrack);
    } catch {}

    return () => {
      try {
        participant.off(ParticipantEvent.TrackSubscribed, onSubscribed);
        participant.off(ParticipantEvent.TrackUnsubscribed, onUnsubscribed);
        participant.off(ParticipantEvent.TrackPublished, onSubscribed);
        participant.off(ParticipantEvent.TrackMuted, attachTrack);
        participant.off(ParticipantEvent.TrackUnmuted, attachTrack);
        const cameraPub = participant.getTrackPublication(Track.Source.Camera);
        if (cameraPub?.track && videoRef.current) {
          cameraPub.track.detach(videoRef.current);
        }
      } catch {}
    };
  }, [participant]);

  return (
    <div className={`relative overflow-hidden bg-gray-900 ${className}`}>
      <video
        ref={videoRef}
        data-pro-camera-identity={participant?.identity || ""}
        className="w-full h-full bg-black"
        autoPlay
        playsInline
        muted
        style={{ display: hasVideo ? 'block' : 'none', width: '100%', height: '100%', objectFit: fit, objectPosition: 'center' }}
      />
      {!hasVideo && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Video className="w-8 h-8 text-gray-600" />
        </div>
      )}
    </div>
  );
};

// ============================================
// Camera Grid (bottom thumbnails)
// ============================================
const CameraGrid = ({ devices, participants, activeDeviceId, previewDeviceId, onSelect, onSwitchCamera }) => {
  const getParticipantForDevice = (deviceNum) =>
    participants.find(p => p.identity === `Camera-${deviceNum}`);

  return (
    <div className="grid grid-cols-5 gap-2 sm:gap-2.5" data-testid="camera-grid">
      {devices.map((device) => {
        const participant = getParticipantForDevice(device.device_number);
        const isActive = device.id === activeDeviceId;
        const isPreview = device.id === previewDeviceId;
        const isPreviewOnly = isPreview && !isActive;
        const isConnected = device.is_connected || !!participant;

        return (
          <div
            key={device.id}
            onClick={() => isConnected && onSelect(device)}
            className={`relative h-[66px] overflow-hidden rounded-xl border-[1.5px] text-left transition-all duration-200 sm:h-[78px]
              ${isPreviewOnly ? 'cursor-pointer border-blue-400/80 bg-blue-500/10 shadow-[0_0_22px_rgba(59,130,246,.2)]'
                : isActive ? 'cursor-pointer border-red-500/70 bg-red-500/10 shadow-[0_0_20px_rgba(239,68,68,.12)]'
                : isConnected ? 'cursor-pointer border-green-500/30 bg-green-500/[0.05] hover:border-white/20'
                : 'cursor-not-allowed border-white/[0.07] bg-white/[0.02] opacity-60'}`}
            data-testid={`camera-cell-${device.device_number}`}
          >
            {participant ? (
              <DirectVideo participant={participant} className="w-full h-full" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
                {isConnected ? <Video className="h-6 w-6 text-gray-600" /> : <VideoOff className="h-6 w-6 text-gray-700" />}
              </div>
            )}
            {/* Camera number + LIVE badge */}
            <div className="absolute top-1.5 left-1.5 flex items-center gap-1">
              <div className={`rounded-md px-1.5 py-0.5 text-[10px] font-extrabold ${isPreviewOnly ? 'bg-blue-600' : isActive ? 'bg-red-600' : 'bg-black/60'} text-white`}>
                {device.device_number}
              </div>
              {isActive && (
                <div className="bg-red-600 px-1.5 py-0.5 rounded text-[10px] text-white flex items-center gap-0.5">
                  <Radio className="w-2.5 h-2.5 animate-pulse" /> LIVE
                </div>
              )}
              {isPreviewOnly && (
                <div className="rounded bg-blue-600 px-1.5 py-0.5 text-[10px] font-black tracking-wide text-white">
                  PREVIEW
                </div>
              )}
            </div>
            {/* Connection indicator */}
            <div className="absolute top-1.5 right-1.5">
              {isConnected ? <Wifi className="w-3 h-3 text-green-400" /> : <WifiOff className="w-3 h-3 text-gray-500" />}
            </div>
            {/* Switch camera button for connected cameras */}
            {isConnected && (
              <button
                onClick={(e) => { e.stopPropagation(); onSwitchCamera(device); }}
                className="absolute bottom-7 right-1.5 w-6 h-6 bg-black/70 hover:bg-black/90 rounded-full flex items-center justify-center transition-colors z-10"
                title="Switch front/back camera"
                data-testid={`switch-cam-${device.device_number}`}
              >
                <SwitchCamera className="w-3 h-3 text-white" />
              </button>
            )}
            {/* Camera name */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent px-2 py-1.5">
              <span className={`text-[10px] font-semibold sm:text-xs ${isConnected ? 'text-gray-100' : 'text-gray-500'}`}>{device.device_name}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ============================================
// Main Video Display
// ============================================
const MainVideoDisplay = ({
  activeParticipant,
  previewDeviceName,
  programDeviceName,
  previewIsProgram,
  outgoingParticipant,
  incomingParticipant,
  transitionState,
  transitionType,
  isLive,
  liveReactions,
  reactionsEnabled,
  reactionSide,
  titheDollarEvents = [],
}) => {
  const videoClassName = 'h-full w-full';
  const renderFeed = (participant, layerClass = '') => (
    <div className={`program-feed relative flex h-full w-full items-center justify-center overflow-hidden bg-black ${layerClass}`}>
      <DirectVideo
        participant={participant}
        fit="contain"
        className={`relative ${videoClassName}`}
      />
    </div>
  );
  const isTransitioning = Boolean(
    transitionState && !transitionState.complete && outgoingParticipant && incomingParticipant
  );

  return (
    <div
      className="relative h-full w-full overflow-hidden rounded-2xl border border-white/[0.08] bg-black"
      data-testid="main-video"
    >
      {/* Keep the active LiveKit video mounted beneath all donation and
          transition overlays so an effect can never replace the preview. */}
      {activeParticipant && renderFeed(activeParticipant, 'z-0')}
      {isTransitioning ? (
        <div key={transitionState.id} className={`absolute inset-0 z-20 transition-${transitionState.type}`}>
          {renderFeed(outgoingParticipant, 'program-feed-outgoing z-10')}
          {renderFeed(incomingParticipant, 'program-feed-incoming z-20')}
        </div>
      ) : !activeParticipant ? (
        <div className="flex h-full w-full items-center justify-center bg-[radial-gradient(circle_at_center,rgba(59,130,246,.08),transparent_55%)]">
          <div className="text-center">
            <div className="mx-auto mb-3 grid h-14 w-14 place-items-center rounded-2xl border border-blue-400/25 bg-blue-500/10">
              <Video className="h-7 w-7 text-blue-300" />
            </div>
            <p className="text-base font-bold text-gray-200">{previewDeviceName ? `Preview · ${previewDeviceName}` : 'Select a camera to preview'}</p>
            <p className="mt-1 text-sm text-gray-600">{previewDeviceName ? `Ready for ${(transitionType || 'cut').toUpperCase()} transition` : 'Connected camera feeds appear below'}</p>
          </div>
        </div>
      ) : null}

      <div className="pointer-events-none absolute left-3 top-3 z-50 flex max-w-[calc(100%-1.5rem)] items-center gap-2">
        <span className={`rounded-md px-2.5 py-1 text-[10px] font-black tracking-[0.16em] text-white shadow-lg ${isLive && previewIsProgram ? 'bg-red-600' : 'bg-blue-600'}`}>
          {isLive && previewIsProgram ? 'PROGRAM' : 'PREVIEW'}
        </span>
        <span className="max-w-48 truncate rounded-md bg-black/65 px-2 py-1 text-[10px] font-bold text-white backdrop-blur-sm">
          {previewDeviceName || 'Select a connected camera'}
        </span>
        {isLive && !previewIsProgram && programDeviceName && (
          <span className="max-w-48 truncate rounded-md border border-red-400/35 bg-black/70 px-2 py-1 text-[10px] font-bold text-red-200 backdrop-blur-sm">
            LIVE: {programDeviceName}
          </span>
        )}
      </div>

      {/* Reactions Overlay */}
      {isLive && reactionsEnabled && <LiveReactionLayer reactions={liveReactions} className="z-40" side={reactionSide} />}
      {isLive && titheDollarEvents.length > 0 && (
        <ShowMeDonationDollarLayer
          events={titheDollarEvents}
          className="z-40"
        />
      )}
    </div>
  );
};

// ============================================
// Audio Settings Panel
// ============================================
const AudioSettingsPanel = ({ activeDevice, onUpdate, audioMode, isLive, onAudioModeChange }) => {
  const [settings, setSettings] = useState({ mic_enabled: true, balance: 50, treble: 50, bass: 50 });

  useEffect(() => {
    if (activeDevice?.audio_settings) {
      setSettings({
        mic_enabled: activeDevice.audio_settings.mic_enabled ?? true,
        balance: activeDevice.audio_settings.balance ?? 50,
        treble: activeDevice.audio_settings.treble ?? 50,
        bass: activeDevice.audio_settings.bass ?? 50,
      });
    }
  }, [activeDevice]);

  const handleChange = (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    onUpdate?.(newSettings);
  };

  const modePicker = (
    <>
      <div className="grid grid-cols-1 gap-1 rounded-lg border border-white/[0.08] bg-black/20 p-1" data-testid="pro-audio-mode-picker">
        {[
          { id: PRO_AUDIO_MODE_RAW, label: 'RAW', detail: 'Stereo · no suppression or automatic gain' },
        ].map((mode) => (
          <button
            key={mode.id}
            type="button"
            disabled={isLive}
            onClick={() => onAudioModeChange?.(mode.id)}
            className={`rounded-md px-2 py-1.5 text-left transition disabled:cursor-not-allowed disabled:opacity-65 ${
              audioMode === mode.id
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-gray-400 hover:bg-white/[0.06] hover:text-white'
            }`}
            data-testid={`pro-audio-mode-${mode.id}`}
          >
            <span className="block text-[11px] font-black">{mode.label}</span>
            <span className={`block text-[8px] ${audioMode === mode.id ? 'text-blue-100' : 'text-gray-600'}`}>{mode.detail}</span>
          </button>
        ))}
      </div>
      {isLive && <p className="text-[9px] text-blue-300">Audio mode is locked until the stream ends.</p>}
    </>
  );

  if (!activeDevice) {
    return (
      <div className="space-y-2 rounded-2xl border border-white/[0.08] bg-[#0d1424] p-3">
        <h3 className="text-sm font-bold text-white">Broadcast Audio</h3>
        {modePicker}
        <p className="text-center text-[10px] font-semibold text-gray-500">Select a camera for microphone controls</p>
      </div>
    );
  }

  return (
    <div className="space-y-2 rounded-2xl border border-white/[0.08] bg-[#0d1424] p-3">
      <div className="flex items-center justify-between">
        <h3 className="text-white font-bold text-sm">Audio Settings</h3>
        <button
          onClick={() => handleChange('mic_enabled', !settings.mic_enabled)}
          className={`p-2 rounded-lg transition-colors ${settings.mic_enabled ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
        >
          {settings.mic_enabled ? <Mic className="w-4 h-4 text-white" /> : <MicOff className="w-4 h-4 text-white" />}
        </button>
      </div>
      {modePicker}
      <div className="space-y-1">
        <div className="flex justify-between text-xs text-gray-400"><span>L</span><span>Balance</span><span>R</span></div>
        <input type="range" min="0" max="100" value={settings.balance} onChange={(e) => handleChange('balance', parseInt(e.target.value))} className="w-full h-1.5 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-500" />
      </div>
      <div className="space-y-1">
        <div className="flex justify-between text-xs text-gray-400"><span>Treble</span><span>{settings.treble}%</span></div>
        <input type="range" min="0" max="100" value={settings.treble} onChange={(e) => handleChange('treble', parseInt(e.target.value))} className="w-full h-1.5 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-green-500" />
      </div>
      <div className="space-y-1">
        <div className="flex justify-between text-xs text-gray-400"><span>Bass</span><span>{settings.bass}%</span></div>
        <input type="range" min="0" max="100" value={settings.bass} onChange={(e) => handleChange('bass', parseInt(e.target.value))} className="w-full h-1.5 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-orange-500" />
      </div>
    </div>
  );
};

const ENERGY_CATEGORIES = [
  { key: "heart", emoji: "❤️" },
  { key: "fire", emoji: "🔥" },
  { key: "laugh", emoji: "😂" },
  { key: "praise", emoji: "🙌" },
];

const ENERGY_CATEGORY_BY_EMOJI = {
  "❤️": "heart",
  "❤️‍🔥": "heart",
  "💜": "heart",
  "😂": "laugh",
  "🤣": "laugh",
  "😭": "laugh",
  "💀": "laugh",
  "🪦": "laugh",
  "🔥": "fire",
  "🔥🔥": "fire",
  "🚀": "fire",
  "🌋": "fire",
  "👏": "praise",
  "🙌": "praise",
  "🙏": "praise",
  "⛪": "praise",
};

const ProEnergyPanel = ({ reactions }) => {
  const latestReactionId = reactions[reactions.length - 1]?.id;
  const [energyPercent, setEnergyPercent] = useState(0);
  const processedReactionIdsRef = useRef(new Set());
  const totalReactionCountRef = useRef(0);
  const cooldownRef = useRef(null);
  const decayRef = useRef(null);
  const counts = ENERGY_CATEGORIES.reduce((result, category) => ({ ...result, [category.key]: 0 }), {});
  reactions.forEach((reaction) => {
    const category = ENERGY_CATEGORY_BY_EMOJI[reaction.emoji];
    if (category) counts[category] += 1;
  });

  useEffect(() => {
    if (latestReactionId === undefined || latestReactionId === null) return undefined;

    clearTimeout(cooldownRef.current);
    clearInterval(decayRef.current);
    const newReactionCount = reactions.reduce((total, reaction) => {
      const reactionId = String(reaction.id);
      if (processedReactionIdsRef.current.has(reactionId)) return total;
      processedReactionIdsRef.current.add(reactionId);
      return total + 1;
    }, 0);
    const previousReactionCount = totalReactionCountRef.current;
    const nextReactionCount = previousReactionCount + newReactionCount;
    totalReactionCountRef.current = nextReactionCount;
    const newEnergyReactionCount = getNewEnergyReactionCount(
      previousReactionCount,
      nextReactionCount,
    );
    if (newEnergyReactionCount > 0) {
      setEnergyPercent((previous) => Math.min(100, previous + (newEnergyReactionCount * 4)));
    }

    if (nextReactionCount < SHOWME_ENERGY_START_REACTION_COUNT) return undefined;

    cooldownRef.current = setTimeout(() => {
      decayRef.current = setInterval(() => {
        setEnergyPercent((previous) => {
          const next = Math.max(0, previous - 2);
          if (next === 0) clearInterval(decayRef.current);
          return next;
        });
      }, 250);
    }, 30000);

    return () => {
      clearTimeout(cooldownRef.current);
      clearInterval(decayRef.current);
    };
  }, [latestReactionId, reactions]);

  const energyLabel = energyPercent >= 80
    ? "Crowd is on fire 🔥"
    : energyPercent >= 45
      ? "Energy is building"
      : energyPercent > 0
        ? "Crowd is warming up"
        : "Waiting for live reactions";

  return (
    <section className="rounded-2xl border border-white/[0.08] bg-[#0d1424] p-3" data-testid="pro-energy-panel">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-sm font-extrabold text-white">
          <Zap className="h-4 w-4 fill-blue-400 text-blue-400" />ShowMeEnergy
        </h3>
        <span className="text-xs font-black text-blue-300">{energyPercent}%</span>
      </div>
      <div className="h-2.5 overflow-hidden rounded-full bg-white/[0.08]">
        <div
          className="h-full rounded-full bg-gradient-to-r from-blue-500 via-blue-400 to-red-500 shadow-[0_0_14px_rgba(59,130,246,.45)] transition-all duration-500"
          style={{ width: `${energyPercent}%` }}
        />
      </div>
      <p className="mb-2 mt-1.5 text-[10px] text-gray-500">{energyLabel}</p>
      <div className="grid grid-cols-4 gap-1.5">
        {ENERGY_CATEGORIES.map((category) => (
          <div key={category.key} className="rounded-xl border border-white/[0.07] bg-white/[0.035] py-1.5 text-center">
            <span className="block text-base leading-none">{category.emoji}</span>
            <span className="mt-1 block text-[10px] font-semibold text-gray-300">{counts[category.key]}</span>
          </div>
        ))}
      </div>
    </section>
  );
};

const getChatAvatarColor = (username = "Viewer") => {
  const colors = ["#2563eb", "#16a34a", "#db2777", "#1d4ed8", "#ea580c"];
  const total = username.split("").reduce((sum, character) => sum + character.charCodeAt(0), 0);
  return colors[total % colors.length];
};

const ProChatPanel = ({ messages, connected, onPinMessage, onMuteUser, onUnmuteUser, label = "Live conversation", aboveChatControl = null }) => {
  const historyRef = useRef(null);
  const [revealedMuteId, setRevealedMuteId] = useState(null);
  const pinnedMessage = [...messages].reverse().find((message) => message.is_pinned) || null;
  const historyMessages = messages.filter((message) => !pinnedMessage || String(message.id) !== String(pinnedMessage.id));
  const latestId = messages[messages.length - 1]?.id;

  useEffect(() => {
    const history = historyRef.current;
    if (!history) return;
    history.scrollTo({ top: history.scrollHeight, behavior: "smooth" });
  }, [latestId]);

  const renderMessage = (message, pinned = false) => {
    const username = message.username || message.user_name || "Viewer";
    const isChatMuted = Boolean(message.is_chat_muted);
    const canMute = Boolean(onMuteUser && message.user_id && !isChatMuted);
    const canUnmute = Boolean(onUnmuteUser && message.user_id && isChatMuted);
    const content = (
      <div className={`flex items-start gap-2.5 ${pinned ? "rounded-xl border border-blue-300/45 bg-transparent p-2" : ""}`}>
          <div
            className="grid h-7 w-7 shrink-0 place-items-center rounded-full border border-white/20 text-[10px] font-extrabold text-white"
            style={{ backgroundColor: getChatAvatarColor(username) }}
          >
            {message.user_picture ? <img src={message.user_picture} alt="" className="h-full w-full rounded-full object-cover" /> : username.charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1">
              <p className="min-w-0 flex-1 truncate text-[10px] font-bold text-gray-300">{username}</p>
              {pinned && <Pin className="h-3 w-3 fill-blue-300 text-blue-300" aria-label="Pinned comment" />}
              {onPinMessage && (
                <button type="button" onClick={() => onPinMessage(message)} className={`inline-flex min-h-6 items-center gap-1 rounded-full border px-2 py-1 text-[9px] font-extrabold transition ${pinned ? "border-blue-300/40 bg-blue-500/20 text-blue-100 hover:bg-blue-500/30" : "border-white/15 bg-black/30 text-gray-300 hover:border-blue-300/40 hover:bg-blue-500/20 hover:text-blue-100"}`} aria-label={`${pinned ? "Unpin" : "Pin"} comment from ${username}`}>
                  <Pin className={`h-3 w-3 ${pinned ? "fill-blue-200" : ""}`} />
                  <span>{pinned ? "Unpin" : "Pin"}</span>
                </button>
              )}
            </div>
            <p className="break-words text-xs leading-4 text-white">{message.message}</p>
          </div>
        </div>
    );

    const canModerate = canMute || canUnmute;
    if (!canModerate) return <React.Fragment key={message.id}>{content}</React.Fragment>;

    return (
      <SwipeChatModerationRow
        key={message.id}
        messageId={message.id}
        username={username}
        isMuted={isChatMuted}
        canModerate={canModerate}
        isRevealed={String(revealedMuteId) === String(message.id)}
        onReveal={setRevealedMuteId}
        onClose={() => setRevealedMuteId(null)}
        onModerate={() => {
          if (isChatMuted) onUnmuteUser(message);
          else onMuteUser(message);
        }}
      >
        {content}
      </SwipeChatModerationRow>
    );
  };

  return (
    <section className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-white/[0.08] bg-[#0d1424] p-4" data-testid="pro-chat-panel">
      {aboveChatControl && (
        <div className="relative z-20 mb-2 shrink-0" data-testid="pro-control-above-chat-history">
          {aboveChatControl}
        </div>
      )}
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-extrabold text-white">{label}</h3>
        <span className={`flex items-center gap-1.5 text-[10px] font-bold ${connected ? "text-green-400" : "text-gray-600"}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${connected ? "bg-green-400" : "bg-gray-600"}`} />
          {connected ? "Live" : "Reconnecting"}
        </span>
      </div>
      {pinnedMessage && <div className="mb-2 shrink-0">{renderMessage(pinnedMessage, true)}</div>}
      <div ref={historyRef} className="flex min-h-0 flex-1 touch-pan-y flex-col gap-3 overflow-y-auto overscroll-contain pr-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" style={{ WebkitOverflowScrolling: "touch" }} data-testid="pro-chat-history">
        {historyMessages.length ? historyMessages.map((message) => renderMessage(message)) : !pinnedMessage ? (
        <div className="m-auto text-center">
          <p className="text-xs font-semibold text-gray-500">Messages will appear here</p>
          <p className="mt-1 text-[10px] text-gray-700">Your audience can join once the event is live.</p>
        </div>
        ) : null}
      </div>
    </section>
  );
};

const ProControlBar = ({
  activeDevice,
  programDevice,
  activeCameraEnabled,
  isLive,
  canSwitchLive,
  transitionType,
  transitionBusy,
  connectedDeviceCount,
  onAudioToggle,
  onSwitchLive,
  onCameraToggle,
  onSwitchCamera,
  onTransitionChange,
  onFocus,
  onQr,
  onShare,
  onExit,
}) => (
  <footer
    className="flex h-16 min-h-16 shrink-0 items-center gap-1.5 overflow-x-auto rounded-xl border border-white/[0.07] bg-[#0b1020] px-2"
    data-testid="pro-mode-control-bar"
  >
    <button
      type="button"
      disabled={!canSwitchLive}
      onClick={onSwitchLive}
      className="flex h-10 shrink-0 items-center gap-1.5 rounded-lg border border-blue-300/50 bg-blue-600 px-4 text-xs font-black text-white shadow-[0_8px_22px_rgba(37,99,235,.3)] transition hover:bg-blue-500 disabled:cursor-not-allowed disabled:border-white/10 disabled:bg-white/[0.04] disabled:text-gray-500 disabled:shadow-none"
      data-testid="pro-switch-live-btn"
      title={!isLive ? 'Select this preview with Go Live' : canSwitchLive ? 'Send the preview camera live' : 'The preview camera is already live'}
    >
      <Radio className="h-4 w-4" />Switch Live
    </button>

    <button
      type="button"
      disabled={!programDevice}
      onClick={onAudioToggle}
      className={`flex h-10 shrink-0 items-center gap-1.5 rounded-lg border px-3 text-xs font-bold transition disabled:cursor-not-allowed disabled:opacity-40 ${
        programDevice?.audio_settings?.mic_enabled
          ? 'border-green-400/25 bg-green-400/10 text-green-200'
          : 'border-white/10 bg-white/[0.04] text-gray-300'
      }`}
    >
      {programDevice?.audio_settings?.mic_enabled ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}
      {programDevice?.audio_settings?.mic_enabled ? 'Mic on' : 'Mic off'}
    </button>

    <button
      type="button"
      disabled={!activeDevice}
      onClick={onCameraToggle}
      className={`flex h-10 shrink-0 items-center gap-1.5 rounded-lg border px-3 text-xs font-bold transition disabled:cursor-not-allowed disabled:opacity-40 ${
        activeCameraEnabled
          ? 'border-white/10 bg-white/[0.04] text-gray-200'
          : 'border-red-400/25 bg-red-400/10 text-red-200'
      }`}
      data-testid="pro-camera-enabled-btn"
    >
      {activeCameraEnabled ? <Camera className="h-4 w-4" /> : <VideoOff className="h-4 w-4" />}
      {activeCameraEnabled ? 'Camera on' : 'Camera off'}
    </button>

    <button
      type="button"
      disabled={!activeDevice}
      onClick={onSwitchCamera}
      className="flex h-10 shrink-0 items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.04] px-3 text-xs font-bold text-gray-200 transition hover:border-blue-400/30 hover:bg-blue-500/10 disabled:cursor-not-allowed disabled:opacity-40"
      data-testid="pro-switch-camera-btn"
    >
      <SwitchCamera className="h-4 w-4 text-blue-300" />Flip camera
    </button>

    <div className="flex shrink-0 items-center gap-0.5 rounded-lg border border-white/[0.08] bg-white/[0.04] p-1">
      {TRANSITIONS.map((transition) => (
        <button
          key={transition.id}
          type="button"
          disabled={transitionBusy}
          onClick={() => onTransitionChange(transition.id)}
          className={`h-8 rounded-md px-2 text-[9px] font-black transition disabled:cursor-wait disabled:opacity-60 ${
            transitionType === transition.id
              ? 'bg-blue-600 text-white shadow-[0_4px_14px_rgba(59,130,246,.25)]'
              : 'text-gray-500 hover:bg-white/[0.05] hover:text-white'
          }`}
          title={`${transition.name} transition`}
        >
          {transition.icon}
        </button>
      ))}
    </div>

    <button
      type="button"
      disabled={!activeDevice || !activeCameraEnabled}
      onClick={onFocus}
      className="flex h-10 shrink-0 items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.04] px-3 text-xs font-bold text-gray-200 transition hover:border-blue-400/30 hover:bg-blue-500/10 disabled:cursor-not-allowed disabled:opacity-40"
      data-testid="pro-showme-focus-btn"
    >
      <span className="h-2.5 w-2.5 rounded-full bg-blue-500 shadow-[0_0_10px_rgba(59,130,246,.85)]" />
      <Focus className="h-3.5 w-3.5 text-blue-300" />ShowMeFocus
    </button>

    <button
      type="button"
      onClick={onQr}
      className="flex h-10 shrink-0 items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.04] px-3 text-xs font-bold text-gray-200 transition hover:border-blue-400/30 hover:bg-blue-500/10"
      data-testid="pro-qr-cameras-btn"
    >
      <QrCode className="h-4 w-4 text-blue-300" />QR Cameras
      <span className={`rounded-full px-1.5 py-0.5 text-[9px] ${connectedDeviceCount ? 'bg-green-400/15 text-green-300' : 'bg-white/[0.07] text-gray-500'}`}>
        {connectedDeviceCount}/5
      </span>
    </button>

    <button
      type="button"
      onClick={onShare}
      className="flex h-10 shrink-0 items-center gap-1.5 rounded-lg border border-blue-400/30 bg-blue-500/10 px-4 text-xs font-extrabold text-blue-100 transition hover:bg-blue-500/20"
      data-testid="pro-share-btn"
      aria-label="Share this live event"
    >
      <Share2 className="h-4 w-4 text-blue-300" />Share
    </button>

    <button
      type="button"
      onClick={onExit}
      className="sticky right-0 ml-auto flex h-10 shrink-0 items-center gap-1.5 rounded-lg border border-blue-400/30 bg-[#122342] px-4 text-xs font-extrabold text-blue-100 shadow-[-12px_0_18px_#0b1020] transition hover:bg-blue-500/20"
      data-testid="pro-exit-btn"
      aria-label="Exit Pro Mode and return to Creator Studio"
    >
      <LogOut className="h-4 w-4 text-blue-300" />Exit
    </button>
  </footer>
);

const ShowMeFocusMenu = ({
  device,
  zoom,
  torchEnabled,
  onAutoFocus,
  onZoomChange,
  onTorchToggle,
  onSwitchCamera,
  onClose,
}) => {
  if (!device) return null;
  return (
    <CreatorSettingsPortal>
    <section
      className="fixed bottom-[78px] left-1/2 z-[120] w-[min(34rem,calc(100%-2rem))] -translate-x-1/2 rounded-2xl border border-blue-400/30 bg-[#0b1020]/95 p-4 shadow-[0_20px_60px_rgba(0,0,0,.55)] backdrop-blur-xl"
      aria-label={`ShowMeFocus controls for ${device.device_name}`}
      data-testid="pro-showme-focus-menu"
      data-creator-front-layer="true"
    >
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="flex items-center gap-2 font-extrabold text-white"><Focus className="h-4 w-4 text-blue-300" />ShowMeFocus</h2>
          <p className="mt-0.5 text-xs text-gray-400">Controlling {device.device_name}</p>
        </div>
        <button type="button" onClick={onClose} className="rounded-lg p-2 text-gray-400 hover:bg-white/5 hover:text-white" aria-label="Close ShowMeFocus controls">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
        <label className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-3">
          <span className="mb-2 flex items-center justify-between text-xs font-bold text-gray-200"><span>Manual zoom</span><span className="font-mono text-blue-300">{zoom.toFixed(1)}×</span></span>
          <input
            type="range"
            min="1"
            max="5"
            step="0.01"
            value={zoom}
            onChange={(event) => onZoomChange(Number(event.target.value))}
            className="w-full accent-blue-500"
            style={{ touchAction: 'none' }}
            aria-label={`Zoom ${device.device_name}`}
          />
        </label>
        <div className="grid grid-cols-3 gap-2">
          <button type="button" onClick={onAutoFocus} className="rounded-xl border border-blue-400/25 bg-blue-500/10 px-3 py-2 text-xs font-bold text-blue-100 hover:bg-blue-500/20">
            <Focus className="mx-auto mb-1 h-4 w-4" />Auto focus
          </button>
          <button type="button" onClick={onTorchToggle} className={`rounded-xl border px-3 py-2 text-xs font-bold ${torchEnabled ? 'border-yellow-300/40 bg-yellow-300/15 text-yellow-100' : 'border-white/10 bg-white/[0.04] text-gray-200'}`}>
            <Sun className="mx-auto mb-1 h-4 w-4" />Back light
          </button>
          <button type="button" onClick={onSwitchCamera} className="rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2 text-xs font-bold text-gray-200 hover:bg-white/[0.08]">
            <SwitchCamera className="mx-auto mb-1 h-4 w-4" />Switch
          </button>
        </div>
      </div>
    </section>
    </CreatorSettingsPortal>
  );
};


// ============================================
// LiveKit Room Content (uses hooks inside room context)
// ============================================
const ProModeRoomContent = ({
  devices, activeDeviceId, previewDeviceId, isLive,
  onPreviewSelect, onSwitchCamera, onAudioUpdate, transitionState, transitionType,
  chatMessages, liveReactions, reactionsEnabled, chatConnected,
  onCommandTransportReady, onConnectedCameraNumbers, orientation, controlBar,
  onPinMessage, onMuteUser, onUnmuteUser, reactionSide,
  titheDollarEvents,
  conversationLabel,
  showConversationPanel = true,
  aboveChatControl = null,
  audioMode = PRO_AUDIO_MODE_RAW,
  onAudioModeChange,
}) => {
  const participants = useRemoteParticipants();
  const room = useRoomContext();
  const [trackRevision, forceUpdate] = useState(0);
  const missingCameraTrackSinceRef = useRef(new Map());
  const cameraRepublishRequestedAtRef = useRef(new Map());
  const cameraParticipantKey = participants
    .map((participant) => participant.identity)
    .filter((identity) => /^Camera-\d+$/.test(identity))
    .sort()
    .join('|');

  useEffect(() => {
    if (!room) return undefined;
    const sendCommand = async (message, deviceNumber = null) => {
      const options = {
        reliable: true,
        topic: PRO_MODE_COMMAND_TOPIC,
      };
      if (deviceNumber) options.destinationIdentities = [`Camera-${deviceNumber}`];
      await room.localParticipant.publishData(
        new TextEncoder().encode(JSON.stringify(message)),
        options,
      );
    };
    onCommandTransportReady?.(sendCommand);
    return () => onCommandTransportReady?.(null);
  }, [onCommandTransportReady, room]);

  useEffect(() => {
    const cameraNumbers = cameraParticipantKey
      ? cameraParticipantKey.split('|').map((identity) => Number(identity.replace('Camera-', '')))
      : [];
    onConnectedCameraNumbers?.(cameraNumbers);
  }, [cameraParticipantKey, onConnectedCameraNumbers]);

  // A camera participant can remain connected after a mobile browser silently
  // drops its published video track. Detect that half-connected state from the
  // control panel and ask only the affected phone to republish. The grace and
  // cooldown prevent normal subscription startup from causing restart loops.
  useEffect(() => {
    if (!room) return undefined;

    const checkCameraPublications = () => {
      const now = Date.now();
      const connectedIdentities = new Set();

      participants.forEach((participant) => {
        if (!/^Camera-\d+$/.test(participant.identity || '')) return;
        connectedIdentities.add(participant.identity);
        const publication = participant.getTrackPublication(Track.Source.Camera);
        const mediaTrack = publication?.track?.mediaStreamTrack;
        const healthy = Boolean(
          publication
          && publication.track
          && !publication.isMuted
          && mediaTrack?.readyState !== 'ended'
        );

        if (healthy) {
          missingCameraTrackSinceRef.current.delete(participant.identity);
          cameraRepublishRequestedAtRef.current.delete(participant.identity);
          return;
        }

        const missingSince = missingCameraTrackSinceRef.current.get(participant.identity) || now;
        missingCameraTrackSinceRef.current.set(participant.identity, missingSince);
        const lastRequestAt = cameraRepublishRequestedAtRef.current.get(participant.identity) || 0;
        if (!shouldRequestProCameraRepublish({ missingSince, lastRequestAt, now })) return;

        cameraRepublishRequestedAtRef.current.set(participant.identity, now);
        room.localParticipant.publishData(
          new TextEncoder().encode(JSON.stringify({ type: 'ensure_camera_published' })),
          {
            reliable: true,
            topic: PRO_MODE_COMMAND_TOPIC,
            destinationIdentities: [participant.identity],
          },
        ).catch((recoveryError) => {
          console.warn('[ProControl] camera republish request failed', {
            camera: participant.identity,
            error: String(recoveryError),
          });
        });
      });

      Array.from(missingCameraTrackSinceRef.current.keys()).forEach((identity) => {
        if (!connectedIdentities.has(identity)) missingCameraTrackSinceRef.current.delete(identity);
      });
      Array.from(cameraRepublishRequestedAtRef.current.keys()).forEach((identity) => {
        if (!connectedIdentities.has(identity)) cameraRepublishRequestedAtRef.current.delete(identity);
      });
    };

    checkCameraPublications();
    const interval = window.setInterval(checkCameraPublications, 1000);
    return () => window.clearInterval(interval);
  }, [participants, room, trackRevision]);

  // Force re-render when room emits track events
  useEffect(() => {
    if (!room) return;
    const handler = () => forceUpdate(v => v + 1);
    try {
      room.on(RoomEvent.TrackSubscribed, handler);
      room.on(RoomEvent.TrackUnsubscribed, handler);
      room.on(RoomEvent.TrackPublished, handler);
      room.on(RoomEvent.ParticipantConnected, handler);
      room.on(RoomEvent.ParticipantDisconnected, handler);
    } catch {}
    return () => {
      try {
        room.off(RoomEvent.TrackSubscribed, handler);
        room.off(RoomEvent.TrackUnsubscribed, handler);
        room.off(RoomEvent.TrackPublished, handler);
        room.off(RoomEvent.ParticipantConnected, handler);
        room.off(RoomEvent.ParticipantDisconnected, handler);
      } catch {}
    };
  }, [room]);

  const activeDevice = devices.find(d => d.id === activeDeviceId);
  const previewDevice = devices.find(d => d.id === previewDeviceId) || activeDevice;
  const previewParticipant = previewDevice
    ? participants.find(p => p.identity === `Camera-${previewDevice.device_number}`)
    : null;
  const outgoingDevice = transitionState
    ? devices.find((device) => device.id === transitionState.fromDeviceId)
    : null;
  const incomingDevice = transitionState
    ? devices.find((device) => device.id === transitionState.toDeviceId)
    : null;
  const outgoingParticipant = outgoingDevice
    ? participants.find((participant) => participant.identity === `Camera-${outgoingDevice.device_number}`)
    : null;
  const incomingParticipant = incomingDevice
    ? participants.find((participant) => participant.identity === `Camera-${incomingDevice.device_number}`)
    : null;

  // Keep every input connected and visible, but decode full quality only for
  // the program, preview, and transition monitors. Inactive thumbnails use the
  // publisher's low simulcast layer so five cameras cannot starve program audio
  // or freeze the large video monitors.
  useEffect(() => {
    const activeIdentity = activeDevice ? `Camera-${activeDevice.device_number}` : null;
    const previewIdentity = previewDevice ? `Camera-${previewDevice.device_number}` : null;
    const transitionDeviceIds = new Set(
      transitionState && !transitionState.complete
        ? [transitionState.fromDeviceId, transitionState.toDeviceId]
        : []
    );
    const transitionIdentities = devices
      .filter((device) => transitionDeviceIds.has(device.id))
      .map((device) => `Camera-${device.device_number}`);
    participants.forEach((participant) => {
      if (!/^Camera-\d+$/.test(participant.identity || '')) return;
      const publication = participant.getTrackPublication(Track.Source.Camera);
      if (!publication) return;
      const plan = getProControlSubscription({
        participantIdentity: participant.identity,
        activeIdentity,
        previewIdentity,
        transitionIdentities,
      });
      publication.setVideoQuality?.(plan.quality === 'high' ? VideoQuality.HIGH : VideoQuality.LOW);
      publication.setVideoFPS?.(plan.fps);
      publication.setEnabled?.(true);
      if (publication.isSubscribed !== plan.subscribed) {
        publication.setSubscribed?.(plan.subscribed);
      }
    });
  }, [activeDevice, cameraParticipantKey, devices, orientation, participants, previewDevice, trackRevision, transitionState]);

  return (
    <>
      <RoomAudioRenderer />
      <div className="flex min-h-0 flex-1 gap-3 overflow-hidden p-3">
        {/* Main Preview & Camera Grid. Keep the monitor large while reserving
            space for audio, energy, and conversation controls. */}
        <div className="flex min-w-0 flex-[1.75] flex-col gap-3 overflow-hidden">
          <div className="grid min-h-0 flex-1 grid-cols-2 gap-2" data-testid="pro-video-monitors">
            <MainVideoDisplay
              activeParticipant={previewParticipant}
              previewDeviceName={previewDevice?.device_name}
              programDeviceName={activeDevice?.device_name}
              previewIsProgram={false}
              outgoingParticipant={null}
              incomingParticipant={null}
              transitionState={null}
              transitionType={transitionType}
              isLive={false}
              liveReactions={[]}
              reactionsEnabled={false}
              reactionSide={reactionSide}
              titheDollarEvents={[]}
            />
            <MainVideoDisplay
              activeParticipant={isLive ? participants.find((participant) => participant.identity === `Camera-${activeDevice?.device_number}`) : null}
              previewDeviceName={isLive ? activeDevice?.device_name : "Waiting for Go Live"}
              programDeviceName={activeDevice?.device_name}
              previewIsProgram={true}
              outgoingParticipant={outgoingParticipant}
              incomingParticipant={incomingParticipant}
              transitionState={transitionState}
              transitionType={transitionType}
              isLive={isLive}
              liveReactions={liveReactions}
              reactionsEnabled={reactionsEnabled}
              reactionSide={reactionSide}
              titheDollarEvents={titheDollarEvents}
            />
          </div>
          <div className="flex-shrink-0">
            <CameraGrid
              devices={devices}
              participants={participants}
              activeDeviceId={activeDeviceId}
              previewDeviceId={previewDevice?.id}
              onSelect={onPreviewSelect}
              onSwitchCamera={onSwitchCamera}
            />
          </div>
          {controlBar}
        </div>

        {/* Right Panel - Controls */}
        <aside className="flex min-h-0 min-w-[238px] flex-1 flex-col gap-2.5 overflow-hidden" data-testid="pro-side-controls">
          <AudioSettingsPanel
            activeDevice={activeDevice}
            onUpdate={(settings) => onAudioUpdate(settings, activeDevice)}
            audioMode={audioMode}
            isLive={isLive}
            onAudioModeChange={onAudioModeChange}
          />
          <ProEnergyPanel reactions={liveReactions} />
          {showConversationPanel && (
            <ProChatPanel messages={chatMessages} connected={chatConnected} onPinMessage={onPinMessage} onMuteUser={onMuteUser} onUnmuteUser={onUnmuteUser} label={conversationLabel} aboveChatControl={aboveChatControl} />
          )}
        </aside>
      </div>
    </>
  );
};

// ============================================
// Main Pro Mode Control Panel
// ============================================
const ProModeControlPanel = ({ user, onLogout }) => {
  const { eventId } = useParams();
  const navigate = useNavigate();

  const [session, setSession] = useState(null);
  const [event, setEvent] = useState(null);
  const [devices, setDevices] = useState([]);
  const [activeDeviceId, setActiveDeviceId] = useState(null);
  const [previewDeviceId, setPreviewDeviceId] = useState(null);
  const [transitionType, setTransitionType] = useState("cut");
  const [transitionState, setTransitionState] = useState(null);
  const [transitionBusy, setTransitionBusy] = useState(false);
  const [cameraEnabledByDevice, setCameraEnabledByDevice] = useState({});
  const [isLive, setIsLive] = useState(false);
  const [viewerCount, setViewerCount] = useState(0);
  const [streamTime, setStreamTime] = useState(0);
  const [titheAmount, setTitheAmount] = useState(0);
  const {
    notifications: tipNotifications,
    enqueueTipNotification,
  } = useCreatorTipNotifications();
  const [loading, setLoading] = useState(true);
  const [livekitToken, setLivekitToken] = useState(null);
  const [livekitUrl, setLivekitUrl] = useState(null);
  const [liveKitRoomGeneration, setLiveKitRoomGeneration] = useState(0);
  const [titheDollarEvents, setTitheDollarEvents] = useState([]);
  const [showFocusMenu, setShowFocusMenu] = useState(false);
  const [zoomByDevice, setZoomByDevice] = useState({});
  const [torchByDevice, setTorchByDevice] = useState({});
  const [audioMode, setAudioMode] = useState(PRO_AUDIO_MODE_RAW);

  // Chat & Reactions
  const [chatMessages, setChatMessages] = useState([]);
  const [, setMutedChatUserIds] = useState([]);
  const [chatConnected, setChatConnected] = useState(false);
  const [liveReactions, setLiveReactions] = useState([]);
  const [educationQuestions, setEducationQuestions] = useState([]);
  const [educationSpeakers, setEducationSpeakers] = useState([]);
  const [educationSpeakerBusyId, setEducationSpeakerBusyId] = useState(null);
  const [audienceSpeakerToggleBusy, setAudienceSpeakerToggleBusy] = useState(false);

  const commandTransportRef = useRef(null);
  const proModeWsRef = useRef(null);
  const chatWsRef = useRef(null);
  const streamTimerRef = useRef(null);
  const seenIdsRef = useRef(new Set());
  const animatedTitheIdsRef = useRef(new Set());
  const titheSnapshotReadyRef = useRef(false);
  const zoomCommandStateRef = useRef({
    timer: null,
    lastSentAt: 0,
    pending: null,
  });
  const liveKitRecoveryTimerRef = useRef(null);
  const liveKitRecoveryAttemptRef = useRef(0);
  const transitionInProgressRef = useRef(false);
  const transitionSequenceRef = useRef(0);
  const transitionTypeRef = useRef("cut");
  const transitionSaveTimerRef = useRef(null);
  const transitionSaveSequenceRef = useRef(0);

  // Private creator delivery and recovery use the same donation IDs so a
  // reconnect cannot replay an already received green-dollar burst.
  const enqueueTitheEffect = useCallback((
    id,
    dollarEffectCount,
    animationStartedAt = null,
    leadingEmojis = [],
  ) => {
    const safeId = String(id || (Date.now() + Math.random()));
    if (animatedTitheIdsRef.current.has(safeId)) return;
    animatedTitheIdsRef.current.add(safeId);
    setTitheDollarEvents((current) => {
      const effect = {
        id: safeId,
        dollarCount: normalizeShowMeDonationDollarCount(dollarEffectCount),
        leadingEmojis,
        animationStartedAt,
      };
      const existingIndex = current.findIndex((item) => String(item.id) === safeId);
      if (existingIndex === -1) return [...current, effect].slice(-100);
      const next = [...current];
      next[existingIndex] = { ...next[existingIndex], ...effect };
      return next;
    });
  }, []);
  const cancelLiveKitRecovery = useCallback(() => {
    window.clearTimeout(liveKitRecoveryTimerRef.current);
    liveKitRecoveryTimerRef.current = null;
  }, []);

  const scheduleLiveKitRecovery = useCallback(() => {
    if (liveKitRecoveryTimerRef.current || !eventId) return;

    const attemptRecovery = async () => {
      liveKitRecoveryTimerRef.current = null;
      if (!navigator.onLine) {
        liveKitRecoveryTimerRef.current = window.setTimeout(attemptRecovery, 2000);
        return;
      }
      try {
        const response = await axiosInstance.post(`/pro-mode/control-panel/connect?event_id=${eventId}`);
        setLivekitToken(response.data.livekit_token);
        setLivekitUrl(response.data.livekit_url);
        setLiveKitRoomGeneration((generation) => generation + 1);
        liveKitRecoveryAttemptRef.current += 1;
        console.info('[ProControl] LiveKit room recovery started', {
          attempt: liveKitRecoveryAttemptRef.current,
        });
      } catch (recoveryError) {
        liveKitRecoveryAttemptRef.current += 1;
        const retryDelay = Math.min(10000, 1000 * (2 ** liveKitRecoveryAttemptRef.current));
        console.warn('[ProControl] LiveKit recovery request failed; retrying', {
          retryDelay,
          error: String(recoveryError),
        });
        liveKitRecoveryTimerRef.current = window.setTimeout(attemptRecovery, retryDelay);
      }
    };

    const initialDelay = Math.min(5000, 1000 * (2 ** liveKitRecoveryAttemptRef.current));
    liveKitRecoveryTimerRef.current = window.setTimeout(attemptRecovery, initialDelay);
  }, [eventId]);

  useEffect(() => () => cancelLiveKitRecovery(), [cancelLiveKitRecovery]);

  const registerCommandTransport = useCallback((transport) => {
    commandTransportRef.current = transport;
  }, []);

  const syncConnectedCameras = useCallback((cameraNumbers) => {
    const connected = new Set(cameraNumbers);
    setDevices((current) => {
      let changed = false;
      const next = current.map((device) => {
        const isConnected = connected.has(device.device_number);
        if (device.is_connected === isConnected) return device;
        changed = true;
        return { ...device, is_connected: isConnected };
      });
      return changed ? next : current;
    });
  }, []);

  const sendProModeCommand = useCallback(async (message, device = null) => {
    if (PRO_MODE_SOCKET_COMMAND_TYPES.has(message.type) && proModeWsRef.current?.readyState === WebSocket.OPEN) {
      proModeWsRef.current.send(JSON.stringify({ ...message, device_id: message.device_id || device?.id }));
      return;
    }
    if (commandTransportRef.current) {
      await commandTransportRef.current(message, device?.device_number || null);
      return;
    }
    if (proModeWsRef.current?.readyState === WebSocket.OPEN) {
      proModeWsRef.current.send(JSON.stringify({ ...message, device_id: message.device_id || device?.id }));
      return;
    }
    throw new Error('Pro Mode command channel is not ready');
  }, []);

  // Init devices 1-5
  useEffect(() => {
    const init = [];
    for (let i = 1; i <= 5; i++) {
      init.push({
        id: `${eventId}-device-${i}`,
        device_number: i,
        device_name: `Camera ${i}`,
        is_connected: false,
        is_active: false,
        audio_settings: { mic_enabled: false, speaker_muted: true, mode: PRO_AUDIO_MODE_RAW, balance: 50, treble: 50, bass: 50 }
      });
    }
    setDevices(init);
  }, [eventId]);

  const handleControlSocketMessage = useCallback((message) => {
    try {
      const data = JSON.parse(message.data);
      if (data.type === 'session_state' && Array.isArray(data.session?.devices)) {
        setAudioMode(normalizeProAudioMode(data.session?.audio_mode));
        setDevices((current) => current.map((slot) => {
          const saved = data.session.devices.find((device) => Number(device.device_number) === slot.device_number);
          return saved ? { ...slot, ...saved, id: `${eventId}-device-${slot.device_number}` } : slot;
        }));
      }
      if (data.type === 'device_connected') {
        setDevices((current) => current.map((slot) => (
          slot.device_number === Number(data.device_number)
            ? { ...slot, id: `${eventId}-device-${slot.device_number}`, is_connected: true }
            : slot
        )));
        toast.success(`Camera ${data.device_number} connected`);
      }
      if (data.type === 'device_socket_reconnecting' || data.type === 'device_disconnected') {
        // LiveKit remains the source of truth for video. Do not remove a
        // camera because its auxiliary command socket is reconnecting.
        console.info(`[ProControl] Camera ${data.device_number} command socket is reconnecting`);
      }
    } catch (socketError) {
      console.warn('[ProControl] ignored invalid socket message:', socketError);
    }
  }, [eventId]);

  const wsBase = BACKEND_URL.replace(/\/api\/?$/, '').replace(/^http:/, 'ws:').replace(/^https:/, 'wss:');
  const controlSocketUrl = `${wsBase}/api/ws/pro-mode/control-panel/${eventId}`;
  useResilientWebSocket({
    url: controlSocketUrl,
    enabled: Boolean(session),
    socketRef: proModeWsRef,
    onMessage: handleControlSocketMessage,
  });

  // Fetch session
  useEffect(() => {
    const fetchData = async () => {
      try {
        const eventRes = await axiosInstance.get(`/events/${eventId}`);
        setEvent(eventRes.data);

        let sessionData;
        try {
          const sessionRes = await axiosInstance.get(`/pro-mode/session/${eventId}`);
          sessionData = sessionRes.data;
        } catch {
          const createRes = await axiosInstance.post(`/pro-mode/session/create?event_id=${eventId}`);
          sessionData = createRes.data;
        }
        setSession(sessionData);
        setAudioMode(normalizeProAudioMode(sessionData.audio_mode));
        const savedTransition = sessionData.transition_type || "cut";
        transitionTypeRef.current = savedTransition;
        setTransitionType(savedTransition);
        setIsLive(Boolean(sessionData.is_live || eventRes.data.status === "live"));

        const cpRes = await axiosInstance.post(`/pro-mode/control-panel/connect?event_id=${eventId}`);
        setLivekitToken(cpRes.data.livekit_token);
        setLivekitUrl(cpRes.data.livekit_url);

        if (sessionData.devices) {
          setDevices(prev => {
            const updated = [...prev];
            sessionData.devices.forEach(d => {
              const idx = updated.findIndex(u => u.device_number === d.device_number);
              if (idx !== -1) updated[idx] = { ...updated[idx], ...d };
            });
            return updated;
          });
          const active = sessionData.devices.find(d => d.is_active);
          if (active) {
            setActiveDeviceId(active.id);
            setPreviewDeviceId(active.id);
          }
        }
        setLoading(false);
      } catch (error) {
        console.error("Failed to load Pro Mode:", error);
        toast.error("Failed to load Pro Mode session");
        setLoading(false);
      }
    };
    fetchData();
  }, [eventId]);

  // Chat & Reactions WebSocket
  useEffect(() => {
    if (!event) return;

    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = BACKEND_URL.replace(/^https?:\/\//, '').replace(/\/api$/, '');
    let creatorSessionToken = "";
    try {
      creatorSessionToken = localStorage.getItem("session_token") || "";
    } catch (_) {}
    const creatorAuth = creatorSessionToken
      ? `&token=${encodeURIComponent(creatorSessionToken)}`
      : "";
    const chatWsUrl = `${wsProtocol}//${wsHost}/api/ws/chat/${eventId}?role=creator${creatorAuth}`;
    let pingInterval;
    let reconnectTimeout;
    let cancelled = false;

    const connectChatWs = () => {
      chatWsRef.current = new WebSocket(chatWsUrl);
      chatWsRef.current.onopen = () => {
        setChatConnected(true);
        pingInterval = setInterval(() => {
          if (chatWsRef.current?.readyState === WebSocket.OPEN) chatWsRef.current.send('ping');
        }, 25000);
      };
      chatWsRef.current.onmessage = (ev) => {
        if (ev.data === 'pong') return;
        try {
          const data = JSON.parse(ev.data);
          const eventMessageId = data.id ?? data.message?.id;
          const dedupeKey = eventMessageId != null ? `${data.type}:${eventMessageId}` : null;
          if (dedupeKey && seenIdsRef.current.has(dedupeKey)) return;
          if (dedupeKey) {
            seenIdsRef.current.add(dedupeKey);
          }
          if (data.type === 'viewer_count' && typeof data.count === 'number') {
            setViewerCount(data.count);
          } else if (data.type === 'message' && data.username) {
            setChatMessages(prev => mergeChatMessages(prev, [{ id: data.id || Date.now() + Math.random(), ...data }], 100));
          }
          if ((data.type === 'new_message' || data.type === 'announcement') && data.message) {
            const msg = data.message;
            setChatMessages(prev => mergeChatMessages(prev, [{
              id: msg.id || Date.now() + Math.random(),
              user_id: msg.user_id,
              username: msg.user_name || msg.username || "Viewer",
              user_picture: msg.user_picture,
              message: msg.message || "",
              color: "#60a5fa",
              timestamp: msg.created_at || data.timestamp,
              sequence: msg.sequence,
              is_pinned: Boolean(msg.is_pinned),
            }], 100));
          }
          if (data.type === 'message_pinned' && data.message_id) {
            setChatMessages((current) => current.map((message) => (
              String(message.id) === String(data.message_id)
                ? { ...message, is_pinned: Boolean(data.is_pinned) }
                : data.is_pinned ? { ...message, is_pinned: false } : message
            )));
          }
          if (data.type === 'message_hidden' && data.message_id) {
            setChatMessages((current) => current.filter(
              (message) => String(message.id) !== String(data.message_id),
            ));
          }
          if (data.type === 'reaction' && data.emoji) {
            setLiveReactions(prev => [...prev.slice(-30), { id: data.id || Date.now() + Math.random(), emoji: data.emoji }]);
          }
          else if (data.type === 'reaction' && data.reaction_type) {
            setLiveReactions(prev => [...prev.slice(-30), {
              id: data.id || Date.now() + Math.random(),
              emoji: data.emoji || REACTION_TYPE_EMOJIS[data.reaction_type] || "\u2764\ufe0f"
            }]);
          }
          if (data.type === 'tithe_total_updated' && typeof data.total_amount === 'number') {
            setTitheAmount(data.total_amount);
          }
          if (data.type === 'tithe_dollar_effect') {
            enqueueTitheEffect(
              data.id,
              data.dollar_effect_count,
              data.animation_started_at,
              data.leading_emojis,
            );
            if (typeof data.tithe_total === 'number') {
              setTitheAmount(data.tithe_total);
            }
            if (data.sender_name) {
              enqueueTipNotification({
                id: data.id,
                senderName: data.sender_name,
                amount: data.amount,
                receivedAt: Date.now(),
              });
            }
          }
          if ((data.type === 'education_private_message' || data.type === 'education_question_waiting') && data.question?.id) {
            setEducationQuestions((current) => (
              current.some((question) => String(question.id) === String(data.question.id))
                ? current
                : [...current, data.question]
            ));
          }
          if (data.type === 'education_private_message_pinned' && data.message_id) {
            setEducationQuestions((current) => current.map((question) => (
              String(question.id) === String(data.message_id)
                ? { ...question, is_pinned: Boolean(data.is_pinned) }
                : data.is_pinned ? { ...question, is_pinned: false } : question
            )));
          }
          if (data.type === 'education_speaker_updated' && data.speaker?.id) {
            setEducationSpeakers((current) => {
              const remaining = current.filter(
                (speaker) => String(speaker.id) !== String(data.speaker.id),
              );
              return ["waiting", "invited", "speaking", "muted"].includes(data.speaker.status)
                ? [...remaining, data.speaker]
                : remaining;
            });
          }
          if (data.type === 'audience_speaker_availability' && typeof data.enabled === 'boolean') {
            setEvent((current) => current ? { ...current, audience_speaker_enabled: data.enabled } : current);
            if (!data.enabled) setEducationSpeakers([]);
          }
        } catch {}
      };
      chatWsRef.current.onclose = () => {
        setChatConnected(false);
        clearInterval(pingInterval);
        if (!cancelled) reconnectTimeout = setTimeout(connectChatWs, 3000);
      };
      chatWsRef.current.onerror = () => setChatConnected(false);
    };
    connectChatWs();
    return () => {
      cancelled = true;
      clearInterval(pingInterval);
      clearTimeout(reconnectTimeout);
      setChatConnected(false);
      if (chatWsRef.current) {
        chatWsRef.current.onclose = null;
        chatWsRef.current.close();
        chatWsRef.current = null;
      }
    };
  }, [eventId, event?.id, event?.chat_enabled, event?.reactions_enabled, event?.audience_speaker_enabled, enqueueTitheEffect, enqueueTipNotification]);

  // Backup real-time snapshot for creator-side overlays if WebSocket reconnects
  // or the control panel is opened after the stream is already live.
  useEffect(() => {
    if (!eventId || !event) return;
    let cancelled = false;

    const fetchSnapshot = async () => {
      const snapshotCategory = (event?.category || "").trim().toLowerCase();
      const education = snapshotCategory === "education";
      const audienceSpeaker = (
        (education || snapshotCategory === "podcast" || snapshotCategory === "religion")
        && event?.audience_speaker_enabled !== false
      );
      const [eventRes, chatRes, reactionRes, titheRes, questionRes, speakerRes, muteRes] = await Promise.allSettled([
        axiosInstance.get(`/events/${eventId}`),
        event.chat_enabled !== false && !education
          ? axiosInstance.get(`/events/${eventId}/chat?limit=25`)
          : Promise.resolve(null),
        event.reactions_enabled !== false ? axiosInstance.get(`/events/${eventId}/reactions/recent?limit=25`) : Promise.resolve(null),
        axiosInstance.get(`/events/${eventId}/tithes/effects?limit=50`),
        education && event.chat_enabled !== false
          ? axiosInstance.get(`/events/${eventId}/questions?limit=100`)
          : Promise.resolve(null),
        audienceSpeaker
          ? axiosInstance.get(`/events/${eventId}/speaker-hands`)
          : Promise.resolve(null),
        event.chat_enabled !== false
          ? axiosInstance.get(`/events/${eventId}/chat-mutes`)
          : Promise.resolve(null),
      ]);
      if (cancelled) return;

      const nextMutedUserIds = muteRes.status === "fulfilled" && Array.isArray(muteRes.value?.data?.muted_user_ids)
        ? muteRes.value.data.muted_user_ids.map(String)
        : [];
      const nextMutedUserSet = new Set(nextMutedUserIds);
      setMutedChatUserIds(nextMutedUserIds);

      if (eventRes.status === "fulfilled" && eventRes.value?.data) {
        const nextEvent = eventRes.value.data;
        setEvent(nextEvent);
        setIsLive(nextEvent.status === "live");
      }

      if (chatRes.status === "fulfilled" && Array.isArray(chatRes.value?.data?.messages)) {
        const snapshot = chatRes.value.data.messages.slice(-25).map((msg) => ({
          id: msg.id,
          user_id: msg.user_id,
          username: msg.user_name || msg.username || "Viewer",
          user_picture: msg.user_picture,
          message: msg.message || "",
          color: "#60a5fa",
          timestamp: msg.created_at,
          sequence: msg.sequence,
          is_pinned: Boolean(msg.is_pinned),
          is_chat_muted: nextMutedUserSet.has(String(msg.user_id || "")),
        }));
        setChatMessages((current) => mergeChatMessages(current, snapshot, 25));
      }

      if (reactionRes.status === "fulfilled" && Array.isArray(reactionRes.value?.data?.reactions)) {
        const fresh = [];
        reactionRes.value.data.reactions.forEach((reaction) => {
          const id = reaction.id || `${reaction.reaction_type}:${reaction.created_at}`;
          const key = `reaction:${id}`;
          if (seenIdsRef.current.has(key)) return;
          seenIdsRef.current.add(key);
          fresh.push({
            id,
            emoji: reaction.emoji || REACTION_TYPE_EMOJIS[reaction.reaction_type] || "\u2764\ufe0f"
          });
        });
        if (fresh.length) {
          setLiveReactions(prev => [...prev.slice(-30), ...fresh].slice(-30));
        }
      }

      if (titheRes.status === "fulfilled" && titheRes.value?.data) {
        const data = titheRes.value.data;
        if (typeof data.tithe_total === "number") {
          setTitheAmount(data.tithe_total);
        }

        const rows = Array.isArray(data.dollar_effects) ? [...data.dollar_effects].reverse() : [];
        rows.forEach((row) => {
          const numericStartedAt = Number(row.animation_started_at);
          const createdAt = Date.parse(row.created_at || "");
          const receivedAt = Number.isFinite(numericStartedAt) ? numericStartedAt : createdAt;
          if (row.sender_name && Number.isFinite(receivedAt)) {
            enqueueTipNotification({
              id: row.id,
              senderName: row.sender_name,
              amount: row.amount,
              receivedAt,
            });
          }
        });
        if (!titheSnapshotReadyRef.current) {
          // A creator can connect to a different serverless instance than the
          // donation request. Recover any just-started animation from storage
          // instead of silently marking the first snapshot as old.
          const now = Date.now();
          rows.forEach((row) => {
            seenIdsRef.current.add(`tithe:${row.id}`);
            const numericStartedAt = Number(row.animation_started_at);
            const createdAt = Date.parse(row.created_at || "");
            const effectStartedAt = Number.isFinite(numericStartedAt)
              ? numericStartedAt
              : createdAt;
            if (Number.isFinite(effectStartedAt) && Math.abs(now - effectStartedAt) <= 30000) {
              enqueueTitheEffect(
                row.id,
                row.dollar_effect_count,
                row.animation_started_at,
                row.leading_emojis,
              );
            } else {
              animatedTitheIdsRef.current.add(String(row.id));
            }
          });
          titheSnapshotReadyRef.current = true;
        } else {
          rows.forEach((row) => {
            enqueueTitheEffect(
              row.id,
              row.dollar_effect_count,
              row.animation_started_at,
              row.leading_emojis,
            );
          });
        }
      }

      if (questionRes.status === "fulfilled" && Array.isArray(questionRes.value?.data?.questions)) {
        setEducationQuestions(questionRes.value.data.questions.map((question) => ({
          ...question,
          is_chat_muted: nextMutedUserSet.has(String(question.user_id || "")),
        })));
      }
      if (speakerRes.status === "fulfilled" && Array.isArray(speakerRes.value?.data?.speakers)) {
        setEducationSpeakers(speakerRes.value.data.speakers);
      }
    };

    fetchSnapshot().catch(() => {});
    const interval = setInterval(
      () => fetchSnapshot().catch(() => {}),
      1500
    );
    return () => {
      cancelled = true;
      clearInterval(interval);
      titheSnapshotReadyRef.current = false;
    };
  }, [eventId, event?.id, event?.chat_enabled, event?.reactions_enabled, event?.audience_speaker_enabled, enqueueTitheEffect, enqueueTipNotification]);

  // Stream timer
  useEffect(() => {
    if (isLive) {
      streamTimerRef.current = setInterval(() => setStreamTime(prev => prev + 1), 1000);
    } else {
      if (streamTimerRef.current) clearInterval(streamTimerRef.current);
    }
    return () => { if (streamTimerRef.current) clearInterval(streamTimerRef.current); };
  }, [isLive]);

  // Selecting a thumbnail changes only the private control-panel preview.
  // Viewers remain on the current program source until Switch Live is pressed.
  const handlePreviewSelect = useCallback((device) => {
    if (!device) {
      toast.error("Camera is not connected");
      return;
    }
    setPreviewDeviceId(device.id);
    setShowFocusMenu(false);
  }, []);

  // Send the prepared preview camera to the live program output.
  const handleDeviceSwitch = async (device) => {
    if (!device.is_connected) { toast.error("Camera is not connected"); return; }
    if (device.id === activeDeviceId) return;
    if (transitionInProgressRef.current) return;
    const selectedTransitionType = transitionTypeRef.current;
    const transition = TRANSITIONS.find(t => t.id === selectedTransitionType);
    const previousDeviceId = activeDeviceId;
    const durationMs = previousDeviceId
      ? getProModeTransitionDuration(transition?.id)
      : 0;
    const midpointMs = Math.round(durationMs / 2);
    const transitionId = ++transitionSequenceRef.current;
    let switchSucceeded = false;
    transitionInProgressRef.current = true;
    setTransitionBusy(true);

    try {
      if (previousDeviceId) {
        setTransitionState({
          id: transitionId,
          fromDeviceId: previousDeviceId,
          toDeviceId: device.id,
          type: selectedTransitionType,
          complete: durationMs === 0,
        });
      }

      // Tell viewers at the start of the effect so their transition timeline
      // stays aligned with the Control Panel instead of beginning at midpoint.
      const liveSwitchRequest = sendProModeCommand({
        type: 'device_switch',
        active_device_id: device.id,
        transition_type: selectedTransitionType,
        audio_delay_ms: midpointMs,
      });
      if (durationMs > 0) {
        // Keep the outgoing source live through the first half of the effect.
        await new Promise((resolve) => setTimeout(resolve, midpointMs));
      }

      // Change the local and broadcast program source at the visual midpoint.
      setDevices(prev => prev.map(d => ({
        ...d, is_active: d.id === device.id,
        audio_settings: { ...d.audio_settings, mic_enabled: d.id === device.id }
      })));
      setActiveDeviceId(device.id);

      const sessionSwitchRequest = axiosInstance.post("/pro-mode/switch-device", {
        event_id: eventId, device_id: device.id, transition_type: selectedTransitionType
      });
      if (durationMs > midpointMs) {
        await new Promise((resolve) => setTimeout(resolve, durationMs - midpointMs));
      }
      // Network acknowledgement must not extend the visual transition or lock
      // the switcher. The real-time command and session write finish in the
      // background; failures are reported without rewinding a completed cut.
      Promise.allSettled([sessionSwitchRequest, liveSwitchRequest]).then(([sessionResult, liveResult]) => {
        if (sessionResult.status === 'rejected') {
          console.error('[ProControl] unable to persist active camera:', sessionResult.reason);
          toast.error('Camera switched, but the saved program source is reconnecting');
        }
        if (liveResult.status === 'rejected') {
          console.warn('[ProControl] immediate device switch message failed; server broadcast remains active', liveResult.reason);
        }
      });
      switchSucceeded = true;
      setTransitionState((current) => (
        current?.id === transitionId ? { ...current, complete: true } : current
      ));
    } catch (switchError) {
      setActiveDeviceId(previousDeviceId);
      setDevices(prev => prev.map(d => ({
        ...d,
        is_active: d.id === previousDeviceId,
        audio_settings: { ...d.audio_settings, mic_enabled: d.id === previousDeviceId }
      })));
      if (previousDeviceId) {
        sendProModeCommand({
          type: 'device_switch',
          active_device_id: previousDeviceId,
          transition_type: 'cut',
        }).catch(() => {});
      }
      toast.error("Failed to switch camera");
      console.error('[ProControl] device switch failed:', switchError);
    } finally {
      // Keep the completed incoming layer mounted. Replacing it with a fresh
      // video element after the animation caused a visible black blink.
      if (!switchSucceeded) setTransitionState(null);
      transitionInProgressRef.current = false;
      setTransitionBusy(false);
    }
  };

  // Device cameras manage their own rotation, so Pro Mode no longer pauses for
  // a saved Portrait/Landscape confirmation.
  const requestGoLive = () => {
    const connectedDevices = devices.filter(d => d.is_connected);
    if (connectedDevices.length === 0) { toast.error("Connect at least one camera to go live"); return; }
    handleGoLive();
  };

  const handleGoLive = async () => {
    try {
      const selectedDevice = devices.find((device) => device.id === previewDeviceId && device.is_connected)
        || devices.find((device) => device.id === activeDeviceId && device.is_connected)
        || devices.find((device) => device.is_connected);
      if (selectedDevice && selectedDevice.id !== activeDeviceId) {
        await axiosInstance.post('/pro-mode/switch-device', {
          event_id: eventId,
          device_id: selectedDevice.id,
          transition_type: 'cut',
        });
        setActiveDeviceId(selectedDevice.id);
        setPreviewDeviceId(selectedDevice.id);
        setDevices((current) => current.map((device) => ({
          ...device,
          is_active: device.id === selectedDevice.id,
        })));
      }
      await axiosInstance.post(`/pro-mode/go-live/${eventId}`);
      if (selectedDevice) {
        await sendProModeCommand({
          type: 'device_switch',
          active_device_id: selectedDevice.id,
        });
      }
      await sendProModeCommand({ type: 'go_live' });
      setIsLive(true);
      toast.success("You are now LIVE!");
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to go live"); }
  };

  // End Stream - broadcasts to all devices, then calls API
  const handleEndStream = async () => {
    // 1. Send end_stream to all connected cameras over LiveKit.
    await sendProModeCommand({ type: 'end_stream' }).catch(() => {});

    // 2. Call backend to delete LiveKit room and update status
    try {
      await axiosInstance.post(`/livekit/end-stream/${eventId}`);
      toast.success("Stream ended on all devices");
    } catch {
      toast.info("Stream ended locally");
    }

    // 3. Reset local state (keep LiveKit connection for preview)
    setIsLive(false);
    setStreamTime(0);
  };

  // Switch front/back camera on a specific device
  const handleSwitchCamera = useCallback(async (device) => {
    try {
      await sendProModeCommand({ type: 'switch_camera', device_id: device.id }, device);
      toast.info(`Switch camera sent to ${device.device_name}`);
    } catch {
      toast.error("Control panel not connected");
    }
  }, [sendProModeCommand]);

  const handleCameraEnabledToggle = useCallback(async (device) => {
    if (!device) {
      toast.error("Select a connected camera first");
      return;
    }
    const enabled = !(cameraEnabledByDevice[device.id] ?? true);
    try {
      await sendProModeCommand({
        type: 'set_camera_enabled',
        device_id: device.id,
        enabled,
      }, device);
      setCameraEnabledByDevice((current) => ({ ...current, [device.id]: enabled }));
      toast.info(`${device.device_name} ${enabled ? "enabled" : "paused"}`);
    } catch {
      toast.error('Control panel not connected');
    }
  }, [cameraEnabledByDevice, sendProModeCommand]);

  const handleShowMeFocus = useCallback(async (device) => {
    if (!device) {
      toast.error("Select a connected camera first");
      return;
    }
    try {
      await sendProModeCommand({ type: 'focus_camera', device_id: device.id }, device);
      toast.success(`ShowMeFocus sent to ${device.device_name}`);
    } catch {
      toast.error('Control panel not connected');
    }
  }, [sendProModeCommand]);

  const flushZoomCommand = useCallback(async () => {
    const state = zoomCommandStateRef.current;
    state.timer = null;
    const pending = state.pending;
    state.pending = null;
    if (!pending) return;
    state.lastSentAt = performance.now();
    try {
      await sendProModeCommand({
        type: 'set_zoom',
        device_id: pending.device.id,
        zoom: pending.zoom,
      }, pending.device);
    } catch {
      toast.error('Unable to update camera zoom');
    }
  }, [sendProModeCommand]);

  const handleZoomChange = useCallback((device, zoom) => {
    if (!device) return;
    setZoomByDevice((current) => ({ ...current, [device.id]: zoom }));
    const state = zoomCommandStateRef.current;
    state.pending = { device, zoom };
    window.clearTimeout(state.timer);
    const elapsed = performance.now() - state.lastSentAt;
    const delay = Math.max(0, SMOOTH_ZOOM_COMMAND_INTERVAL_MS - elapsed);
    state.timer = window.setTimeout(flushZoomCommand, delay);
  }, [flushZoomCommand]);

  useEffect(() => () => {
    window.clearTimeout(zoomCommandStateRef.current.timer);
    zoomCommandStateRef.current.pending = null;
  }, []);

  const handleTorchToggle = useCallback(async (device) => {
    if (!device) return;
    const enabled = !(torchByDevice[device.id] ?? false);
    setTorchByDevice((current) => ({ ...current, [device.id]: enabled }));
    try {
      await sendProModeCommand({ type: 'set_torch', device_id: device.id, enabled }, device);
    } catch {
      setTorchByDevice((current) => ({ ...current, [device.id]: !enabled }));
      toast.error('Unable to update the back light');
    }
  }, [sendProModeCommand, torchByDevice]);

  // Update transition
  const handleTransitionChange = useCallback((newTransition) => {
    if (transitionInProgressRef.current) return;
    if (!TRANSITIONS.some((transition) => transition.id === newTransition)) return;

    // Selection is local and immediate. Persist it shortly afterward so a
    // slow API response can never stall or reorder the next camera switch.
    transitionTypeRef.current = newTransition;
    setTransitionType(newTransition);
    const saveSequence = ++transitionSaveSequenceRef.current;
    window.clearTimeout(transitionSaveTimerRef.current);
    transitionSaveTimerRef.current = window.setTimeout(async () => {
      try {
        await axiosInstance.put(`/pro-mode/session/${eventId}/transition?transition_type=${newTransition}`);
      } catch (transitionError) {
        if (saveSequence === transitionSaveSequenceRef.current) {
          toast.error("Transition selected, but it could not be saved for the next session");
        }
      }
    }, 120);
  }, [eventId]);

  useEffect(() => () => window.clearTimeout(transitionSaveTimerRef.current), []);

  // Update audio
  const handleAudioUpdate = async (settings, targetDevice = null) => {
    const device = targetDevice || devices.find((item) => item.id === activeDeviceId);
    if (!device) return;
    try {
      await axiosInstance.put(`/pro-mode/device/${device.id}/audio?event_id=${eventId}`, settings);
      setDevices((current) => current.map((device) => (
        device.id === (targetDevice?.id || activeDeviceId)
          ? { ...device, audio_settings: { ...device.audio_settings, ...settings } }
          : device
      )));
      await sendProModeCommand({
        type: 'audio_settings_update',
        device_id: device.id,
        settings,
      }, device).catch(() => {});
    } catch {}
  };

  const handleAudioModeChange = useCallback(async (nextMode) => {
    const normalizedMode = normalizeProAudioMode(nextMode);
    if (isLive) {
      toast.info('End the stream before changing the audio profile');
      return;
    }
    const previousMode = audioMode;
    setAudioMode(normalizedMode);
    setDevices((current) => current.map((device) => ({
      ...device,
      audio_settings: { ...device.audio_settings, mode: normalizedMode },
    })));
    try {
      await axiosInstance.put(
        `/pro-mode/session/${eventId}/audio-mode`,
        null,
        { params: { mode: normalizedMode } },
      );
      await sendProModeCommand({
        type: 'audio_settings_update',
        settings: { mode: normalizedMode },
      }).catch(() => {});
      toast.success('RAW audio ready: stereo with all suppression and automatic gain off');
    } catch (modeError) {
      setAudioMode(previousMode);
      setDevices((current) => current.map((device) => ({
        ...device,
        audio_settings: { ...device.audio_settings, mode: previousMode },
      })));
      toast.error(modeError.response?.data?.detail || 'Unable to update the audio mode');
    }
  }, [audioMode, eventId, isLive, sendProModeCommand]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const reactionsEnabled = event?.reactions_enabled !== false;
  const categoryKey = (event?.category || "").trim().toLowerCase();
  const isEducation = categoryKey === "education";
  const isPodcast = categoryKey === "podcast";
  const isReligion = categoryKey === "religion";
  const isAudienceSpeakerEvent = (
    (isEducation || isPodcast || isReligion)
    && event?.audience_speaker_enabled !== false
  );
  const isShowMeCongregation = isVisibleEventCategory(event?.category);
  const experience = getEventExperience(event?.category);
  const connectedDeviceCount = devices.filter((device) => device.is_connected).length;
  const activeDevice = devices.find((device) => device.id === activeDeviceId);
  const previewDevice = resolveProModePreviewDevice(devices, previewDeviceId, activeDeviceId);
  const previewCameraEnabled = previewDevice ? (cameraEnabledByDevice[previewDevice.id] ?? true) : false;
  const previewZoom = previewDevice ? (zoomByDevice[previewDevice.id] ?? 1) : 1;
  const previewTorchEnabled = previewDevice ? (torchByDevice[previewDevice.id] ?? false) : false;
  const canSwitchLive = canSwitchProModePreview({
    isLive,
    previewDevice,
    activeDeviceId,
    transitionBusy,
  });

  const handlePinMessage = async (message) => {
    if (!message?.id) return;
    const shouldPin = !message.is_pinned;
    const previousMessages = chatMessages;
    setChatMessages((current) => current.map((item) => (
      String(item.id) === String(message.id)
        ? { ...item, is_pinned: shouldPin }
        : shouldPin ? { ...item, is_pinned: false } : item
    )));
    try {
      await axiosInstance.post(`/events/${eventId}/chat/${message.id}/pin`, null, { params: { is_pinned: shouldPin } });
    } catch (error) {
      setChatMessages(previousMessages);
      toast.error(error.response?.data?.detail || `Unable to ${shouldPin ? "pin" : "unpin"} comment`);
    }
  };

  const handleMuteChatUser = async (message) => {
    if (!message?.user_id) return;
    try {
      await axiosInstance.post(`/events/${eventId}/chat-mutes/${message.user_id}`);
      const mutedUserId = String(message.user_id);
      setMutedChatUserIds((current) => [...new Set([...current, mutedUserId])]);
      setChatMessages((current) => current.map((item) => (
        String(item.user_id) === mutedUserId ? { ...item, is_chat_muted: true } : item
      )));
      setEducationQuestions((current) => current.map((item) => (
        String(item.user_id) === mutedUserId ? { ...item, is_chat_muted: true } : item
      )));
      toast.success(`${message.username || message.user_name || "Viewer"} can no longer chat in this event.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to mute this viewer's chat");
    }
  };

  const handleUnmuteChatUser = async (message) => {
    if (!message?.user_id) return;
    try {
      await axiosInstance.delete(`/events/${eventId}/chat-mutes/${message.user_id}`);
      const unmutedUserId = String(message.user_id);
      setMutedChatUserIds((current) => current.filter((userId) => userId !== unmutedUserId));
      setChatMessages((current) => current.map((item) => (
        String(item.user_id) === unmutedUserId ? { ...item, is_chat_muted: false } : item
      )));
      setEducationQuestions((current) => current.map((item) => (
        String(item.user_id) === unmutedUserId ? { ...item, is_chat_muted: false } : item
      )));
      toast.success(`${message.username || message.user_name || "Viewer"} can chat again.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to unmute this viewer's chat");
    }
  };

  const handlePinEducationQuestion = async (question) => {
    if (!question?.id) return;
    const shouldPin = !question.is_pinned;
    const previousQuestions = educationQuestions;
    setEducationQuestions((current) => current.map((item) => (
      String(item.id) === String(question.id)
        ? { ...item, is_pinned: shouldPin }
        : shouldPin ? { ...item, is_pinned: false } : item
    )));
    try {
      await axiosInstance.post(`/events/${eventId}/chat/${question.id}/pin`, null, { params: { is_pinned: shouldPin } });
    } catch (error) {
      setEducationQuestions(previousQuestions);
      toast.error(error.response?.data?.detail || `Unable to ${shouldPin ? "pin" : "unpin"} this private message`);
    }
  };

  const deleteEducationQuestion = async (question) => {
    try {
      await axiosInstance.delete(`/events/${eventId}/questions/${question.id}`);
      setEducationQuestions((current) => current.filter((item) => String(item.id) !== String(question.id)));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to delete this question");
    }
  };

  const updateEducationSpeaker = (speaker) => {
    if (!speaker?.id) return;
    setEducationSpeakers((current) => {
      const remaining = current.filter((item) => String(item.id) !== String(speaker.id));
      return ["waiting", "invited", "speaking", "muted"].includes(speaker.status)
        ? [...remaining, speaker]
        : remaining;
    });
  };

  const inviteEducationSpeaker = async (speaker) => {
    setEducationSpeakerBusyId(speaker.id);
    try {
      const { data } = await axiosInstance.post(`/events/${eventId}/speaker-hands/${speaker.id}/invite`);
      updateEducationSpeaker(data?.speaker);
      toast.success(`${speaker.user_name || "Viewer"} can turn on their microphone.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to enable this viewer's microphone");
    } finally {
      setEducationSpeakerBusyId(null);
    }
  };

  const muteEducationSpeaker = async (speaker) => {
    setEducationSpeakerBusyId(speaker.id);
    try {
      const { data } = await axiosInstance.post(`/events/${eventId}/speaker-hands/${speaker.id}/mute`);
      updateEducationSpeaker(data?.speaker);
      toast.success(`${speaker.user_name || "Viewer"}'s microphone is off.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to mute this viewer");
    } finally {
      setEducationSpeakerBusyId(null);
    }
  };

  const endEducationSpeaker = async (speaker) => {
    setEducationSpeakerBusyId(speaker.id);
    try {
      const { data } = await axiosInstance.post(`/events/${eventId}/speaker-hands/${speaker.id}/end`);
      updateEducationSpeaker(data?.speaker);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to end speaker access");
    } finally {
      setEducationSpeakerBusyId(null);
    }
  };

  const toggleAudienceSpeakerAvailability = async () => {
    if (audienceSpeakerToggleBusy) return;
    const previousEnabled = event?.audience_speaker_enabled !== false;
    const nextEnabled = !previousEnabled;
    setAudienceSpeakerToggleBusy(true);
    setEvent((current) => current ? { ...current, audience_speaker_enabled: nextEnabled } : current);
    try {
      const { data } = await axiosInstance.put(`/events/${eventId}/audience-speaker`, {
        enabled: nextEnabled,
      });
      const enabled = Boolean(data?.audience_speaker_enabled);
      setEvent((current) => current ? { ...current, audience_speaker_enabled: enabled } : current);
      if (!enabled) setEducationSpeakers([]);
      const featureCategory = (event?.category || "").trim().toLowerCase();
      const featureName = featureCategory === "podcast"
        ? "Call-In"
        : featureCategory === "religion" ? "Mic" : "Raise Hand";
      toast.success(`${featureName} is ${enabled ? "on" : "off"}.`);
    } catch (error) {
      setEvent((current) => current ? { ...current, audience_speaker_enabled: previousEnabled } : current);
      toast.error(error.response?.data?.detail || "Unable to change audience microphone access");
    } finally {
      setAudienceSpeakerToggleBusy(false);
    }
  };

  const handleShareEvent = async () => {
    const shareUrl = `${window.location.origin}/event/${eventId}`;
    const shareData = {
      title: event?.title || 'ShowMeLive event',
      text: `Watch "${event?.title || 'this event'}" live on ShowMeLive!`,
      url: shareUrl,
    };
    try {
      if (navigator.share && (!navigator.canShare || navigator.canShare(shareData))) {
        await navigator.share(shareData);
        return;
      }
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Event link copied!');
    } catch (shareError) {
      if (shareError?.name === 'AbortError') return;
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Event link copied!');
      } catch {
        toast.error('Unable to share this event');
      }
    }
  };

  if (loading) {
    return (
      <div className="h-screen bg-gray-950 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="relative flex h-screen flex-col overflow-hidden bg-[#080c16]" data-testid="pro-mode-control-panel">
      <style>{transitionStyles}</style>
      {showFocusMenu && (
        <ShowMeFocusMenu
          device={previewDevice}
          zoom={previewZoom}
          torchEnabled={previewTorchEnabled}
          onAutoFocus={() => handleShowMeFocus(previewDevice)}
          onZoomChange={(zoom) => handleZoomChange(previewDevice, zoom)}
          onTorchToggle={() => handleTorchToggle(previewDevice)}
          onSwitchCamera={() => handleSwitchCamera(previewDevice)}
          onClose={() => setShowFocusMenu(false)}
        />
      )}
      {/* Header */}
      <header className="flex h-[62px] shrink-0 items-center gap-3 overflow-x-auto border-b border-white/[0.07] bg-[#0b1020] px-3 sm:px-4">
        <div className="flex min-w-0 shrink-0 items-center gap-3">
          <div className="min-w-0">
            <h1 className="flex items-center gap-2 font-extrabold text-white">
              Pro Mode <span className="rounded-md bg-blue-500/20 px-2 py-1 text-[9px] font-black tracking-widest text-blue-300">MULTI-CAM</span>
            </h1>
            <p className="max-w-36 truncate text-[11px] text-gray-500 sm:max-w-56">{event?.title}</p>
          </div>
        </div>

        <div className="flex min-w-0 flex-1 items-center gap-2">
          {isLive && (
            <>
              <div className="flex items-center gap-1.5 rounded-full bg-red-500 px-3 py-2 shadow-[0_0_18px_rgba(239,68,68,.4)]">
                <Radio className="h-3.5 w-3.5 text-white" />
                <span className="text-xs font-extrabold tracking-wide text-white">LIVE</span>
              </div>
            </>
          )}
          {!isLive && (
            <div className="hidden items-center gap-2 rounded-full border border-white/[0.08] bg-white/[0.04] px-3 py-2 text-xs font-semibold text-gray-300 sm:flex">
              <span className={`h-2 w-2 rounded-full ${connectedDeviceCount ? 'bg-green-400 shadow-[0_0_9px_rgba(74,222,128,.8)]' : 'bg-gray-600'}`} />
              {connectedDeviceCount}/5 cameras
            </div>
          )}
          <div
            className="flex shrink-0 flex-col overflow-hidden rounded-xl border border-white/[0.08] bg-white/[0.04] text-gray-400"
            title="Live viewer count"
            data-testid="pro-viewer-count"
          >
            <LiveViewerRoster
              eventId={eventId}
              viewerCount={viewerCount}
              isCreator
              buttonClassName="px-3 py-1.5 text-xs font-bold text-white"
              testId="pro-live-viewer-count"
            />
            {isLive && (isEducation || isPodcast || isReligion) && (
              <button
                type="button"
                onClick={toggleAudienceSpeakerAvailability}
                disabled={audienceSpeakerToggleBusy}
                className={`flex items-center justify-center gap-1 border-t px-2 py-1 text-[9px] font-black transition active:scale-95 disabled:cursor-wait disabled:opacity-60 ${
                  isAudienceSpeakerEvent
                    ? "border-emerald-400/30 bg-emerald-500/20 text-emerald-100"
                    : "border-white/[0.08] bg-black/20 text-gray-400"
                }`}
                aria-label={`${isAudienceSpeakerEvent ? "Turn off" : "Turn on"} ${isPodcast ? "Call-In" : isReligion ? "Mic" : "Raise Hand"}`}
                title={`${isPodcast ? "Call-In" : isReligion ? "Mic" : "Raise Hand"} ${isAudienceSpeakerEvent ? "on" : "off"}`}
                data-testid="pro-audience-speaker-toggle"
              >
                <span aria-hidden="true">{isPodcast ? "☎️" : isReligion ? "🎙️" : "🖐️"}</span>
                <span>{isAudienceSpeakerEvent ? "ON" : "OFF"}</span>
              </button>
            )}
          </div>
          {event?.tips_enabled !== false && (
            <div
              className="flex shrink-0 flex-col rounded-xl border border-green-400/20 bg-green-400/10 px-3 py-1.5 text-green-300"
              title={experience.tipsLabel}
              data-testid="pro-tithe-total"
            >
              <span className="flex items-center gap-1 text-xs font-extrabold">
                <DollarSign className="h-3.5 w-3.5" />
                {experience.tipsLabel.replace(/^ShowMe/, "")} ${Number(titheAmount || 0).toFixed(2)}
              </span>
            </div>
          )}
          <div className="ml-auto flex shrink-0 flex-col items-center gap-1">
            <Button
              onClick={isLive ? handleEndStream : requestGoLive}
              data-testid="pro-live-btn"
              className={`h-10 shrink-0 rounded-xl px-5 font-bold ${isLive ? 'bg-red-500 shadow-[0_8px_20px_rgba(239,68,68,.25)] hover:bg-red-600' : 'bg-blue-600 hover:bg-blue-500'}`}
            >
              {isLive ? (<><Square className="mr-1 h-4 w-4" /> End</>) : (<><Play className="mr-1 h-4 w-4" /> Go Live</>)}
            </Button>
            {isLive && (
              <div
                className="flex items-center justify-center gap-1 rounded-full border border-white/[0.08] bg-white/[0.04] px-2 py-0.5 font-mono text-[9px] font-bold text-white"
                title="Live stream timer"
                data-testid="pro-stream-timer"
              >
                <Clock3 className="h-3 w-3 text-blue-300" />
                <span>{formatTime(streamTime)}</span>
              </div>
            )}
          </div>
        </div>
      </header>

      {isLive && experience.liveTipIdentity && (
        <CreatorTipNotificationStack
          notifications={tipNotifications}
          className="!left-4 !top-20"
        />
      )}

      {/* LiveKit Room */}
      {livekitToken && livekitUrl ? (
        <LiveKitRoom
          key={`control-panel-${liveKitRoomGeneration}`}
          serverUrl={livekitUrl}
          token={livekitToken}
          connect={true}
          video={false}
          audio={false}
          onConnected={() => {
            cancelLiveKitRecovery();
            liveKitRecoveryAttemptRef.current = 0;
          }}
          onDisconnected={scheduleLiveKitRecovery}
          onError={(connectionError) => {
            console.error('[ProControl] LiveKit connection error:', connectionError);
            scheduleLiveKitRecovery();
          }}
          options={{
            // Subscribe only after the switcher assigns a simulcast layer. This
            // prevents a burst where every newly joined camera briefly arrives
            // at full quality and stalls the program feed.
            adaptiveStream: false,
            autoSubscribe: false,
            disconnectOnPageLeave: false,
            reconnectPolicy: {
              nextRetryDelayInMs: (ctx) => Math.min(1000 * Math.pow(2, ctx.retryCount), 10000),
              maxRetries: 15,
            },
          }}
          className="flex min-h-0 flex-1 flex-col overflow-hidden"
        >
          <ProModeRoomContent
            devices={devices}
            activeDeviceId={activeDeviceId}
            previewDeviceId={previewDevice?.id}
            isLive={isLive}
            onPreviewSelect={handlePreviewSelect}
            onSwitchCamera={handleSwitchCamera}
            onAudioUpdate={handleAudioUpdate}
            transitionState={transitionState}
            transitionType={transitionType}
            chatMessages={isEducation ? [] : chatMessages}
            liveReactions={liveReactions}
            reactionsEnabled={reactionsEnabled}
            reactionSide={isShowMeCongregation ? "right" : "spread"}
            onPinMessage={handlePinMessage}
            onMuteUser={handleMuteChatUser}
            onUnmuteUser={handleUnmuteChatUser}
            titheDollarEvents={isLive && event?.tips_enabled !== false ? titheDollarEvents : []}
            conversationLabel="Live Chat"
            showConversationPanel={!isEducation && event?.chat_enabled !== false}
            aboveChatControl={isLive && isAudienceSpeakerEvent && educationSpeakers.some((speaker) => ["waiting", "invited", "speaking", "muted"].includes(speaker.status)) ? (
              <CreatorAudienceSpeakerControls
                speakerRequests={educationSpeakers}
                onInviteSpeaker={inviteEducationSpeaker}
                onMuteSpeaker={muteEducationSpeaker}
                onEndSpeaker={endEducationSpeaker}
                speakerBusyId={educationSpeakerBusyId}
                speakerMode={isPodcast ? "podcast" : isReligion ? "religion" : "education"}
              />
            ) : null}
            chatConnected={chatConnected}
            onCommandTransportReady={registerCommandTransport}
            onConnectedCameraNumbers={syncConnectedCameras}
            orientation={STREAM_ORIENTATION}
            audioMode={audioMode}
            onAudioModeChange={handleAudioModeChange}
            controlBar={(
              <ProControlBar
                activeDevice={previewDevice}
                programDevice={activeDevice}
                activeCameraEnabled={previewCameraEnabled}
                isLive={isLive}
                canSwitchLive={canSwitchLive}
                transitionType={transitionType}
                transitionBusy={transitionBusy}
                connectedDeviceCount={connectedDeviceCount}
                onSwitchLive={() => previewDevice && handleDeviceSwitch(previewDevice)}
                onAudioToggle={() => activeDevice && handleAudioUpdate({
                  ...activeDevice.audio_settings,
                  mic_enabled: !(activeDevice.audio_settings?.mic_enabled ?? false),
                }, activeDevice)}
                onCameraToggle={() => previewDevice && handleCameraEnabledToggle(previewDevice)}
                onSwitchCamera={() => previewDevice && handleSwitchCamera(previewDevice)}
                onTransitionChange={handleTransitionChange}
                onFocus={() => previewDevice && setShowFocusMenu((open) => !open)}
                onQr={() => navigate(`/pro-mode/${eventId}/qr`)}
                onShare={handleShareEvent}
                onExit={() => navigate('/creator/dashboard')}
              />
            )}
          />
          <StreamDiagnostics label="Pro control" />
        </LiveKitRoom>
      ) : (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 text-blue-500 mx-auto mb-3 animate-spin" />
            <p className="text-white">Connecting...</p>
          </div>
        </div>
      )}

      {isLive && isEducation && (
        <EducationQuestionQueue
          questions={educationQuestions}
          onDelete={deleteEducationQuestion}
          onPinQuestion={handlePinEducationQuestion}
          onMuteUser={handleMuteChatUser}
          onUnmuteUser={handleUnmuteChatUser}
          speakerRequests={educationSpeakers}
          onInviteSpeaker={inviteEducationSpeaker}
          onMuteSpeaker={muteEducationSpeaker}
          onEndSpeaker={endEducationSpeaker}
          speakerBusyId={educationSpeakerBusyId}
          speakerMode={isPodcast ? "podcast" : isReligion ? "religion" : "education"}
        />
      )}

    </div>
  );
};

export default ProModeControlPanel;
